import React from "react";
import { useSearchParams } from "react-router-dom";
import styles from "../components/Dashboard.module.css";
import { auth } from "../firebase/firebase";
import ExperimentDropdown from "../components/ExperimentDropdown";
import LangfuseMetricsCharts from "../components/LangfuseMetricsCharts";
import {
  metricsApi,
  DEMO_DEFAULT_AGENT_ID,
  findPairForAgent,
  groupPairsByDataset,
  ModelComparisonGroup,
  MonitoredPair,
  PairDashboardPayload,
  parsePairId,
  resolveDefaultAgentRunId,
  resolveDefaultDatasetId,
} from "../services/metricsApi";
import {
  buildOptimisticRunPanel,
  getCachedTrendDashboard,
  getCachedRunPanel,
  mergeRunPanelFromFetches,
  setCachedRunPanel,
  setCachedTrendDashboard,
  setRunPanelCacheNamespace,
} from "../services/runPanelCache";

const LangfuseDashboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allPairs, setAllPairs] = React.useState<MonitoredPair[]>([]);
  const [datasets, setDatasets] = React.useState<ReturnType<typeof groupPairsByDataset>>([]);
  const [langfuseHost, setLangfuseHost] = React.useState<string | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = React.useState("");
  const [trendDashboard, setTrendDashboard] = React.useState<PairDashboardPayload | null>(null);
  const [runDashboard, setRunDashboard] = React.useState<PairDashboardPayload | null>(null);
  const [selectedAgentId, setSelectedAgentId] = React.useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const [selectedPairId, setSelectedPairId] = React.useState("");
  const [comparisons, setComparisons] = React.useState<ModelComparisonGroup[]>([]);
  const [pairsLoading, setPairsLoading] = React.useState(true);
  const [dashboardLoading, setDashboardLoading] = React.useState(false);
  const [runLoading, setRunLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<string[]>([]);
  const trendDashboardRef = React.useRef<PairDashboardPayload | null>(null);
  const loadSeqRef = React.useRef(0);

  React.useEffect(() => {
    setRunPanelCacheNamespace(auth.currentUser?.uid);
  }, []);

  React.useEffect(() => {
    trendDashboardRef.current = trendDashboard;
  }, [trendDashboard]);

  const pairsForDataset = React.useMemo(() => {
    return datasets.find((row) => row.id === selectedDatasetId)?.pairs ?? [];
  }, [datasets, selectedDatasetId]);

  React.useEffect(() => {
    let mounted = true;
    const loadPairs = async () => {
      setPairsLoading(true);
      setErrors([]);
      try {
        const { pairs: rows, langfuseHost: host } = await metricsApi.listDashboardPairs();
        if (!mounted) return;
        setAllPairs(rows);
        setLangfuseHost(host);
        const grouped = groupPairsByDataset(rows);
        setDatasets(grouped);
        const fromUrl = searchParams.get("dataset")?.trim() ?? "";
        const match = grouped.find((row) => row.id === fromUrl);
        setSelectedDatasetId(match?.id ?? resolveDefaultDatasetId(grouped));
      } catch {
        if (!mounted) return;
        setErrors(["Failed to load datasets."]);
      } finally {
        if (mounted) setPairsLoading(false);
      }
    };
    void loadPairs();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRunPanels = React.useCallback(
    async (
      pairId: string,
      runId: string,
      agentId: string,
      syncUrl = true,
      trendShell: PairDashboardPayload | null = trendDashboardRef.current,
    ) => {
      const seq = ++loadSeqRef.current;
      setSelectedPairId(pairId);
      setSelectedAgentId(agentId);
      setSelectedRunId(runId);
      setErrors([]);

      const cached = getCachedRunPanel(pairId, runId);
      if (cached) {
        setRunDashboard(cached);
        setRunLoading(false);
      } else if (trendShell) {
        setRunDashboard(buildOptimisticRunPanel(trendShell, agentId, runId));
        setRunLoading(true);
      } else {
        setRunLoading(true);
      }

      if (syncUrl) {
        setSearchParams(
          (prev) => {
            const nextParams = new URLSearchParams(prev);
            nextParams.set("dataset", parsePairId(pairId).datasetId);
            nextParams.set("agent", agentId);
            nextParams.set("run", runId);
            nextParams.delete("pair");
            return nextParams;
          },
          { replace: true },
        );
      }

      try {
        const base =
          cached ??
          (trendShell
            ? buildOptimisticRunPanel(trendShell, agentId, runId)
            : {
                agentId,
                datasetId: parsePairId(pairId).datasetId,
                datasetName: null,
                agentName: null,
                model: null,
                connectorType: null,
                mode: null,
                runId,
                runCompletedAt: null,
                traceCount: 0,
                itemTotal: 0,
                costUsd: null,
                correctnessMetric: null,
                aggregates: {},
                items: [],
                runHistory: [],
                experimentScores: [],
                modelTrends: [],
              });

        const [itemsRes, costUsd] = await Promise.all([
          metricsApi.getPairRunItems(pairId, runId, 1, 100),
          metricsApi.getPairRunCost(pairId, runId),
        ]);
        if (seq !== loadSeqRef.current) return;

        const next = mergeRunPanelFromFetches({
          base,
          items: itemsRes.items,
          itemTotal: itemsRes.total,
          costUsd,
          correctnessMetric: itemsRes.correctnessMetric,
        });
        setCachedRunPanel(pairId, runId, next);
        setRunDashboard(next);
      } catch (err) {
        if (seq !== loadSeqRef.current) return;
        if (!cached) setRunDashboard(null);
        setErrors([err instanceof Error ? err.message : "Failed to load run details."]);
      } finally {
        if (seq === loadSeqRef.current) setRunLoading(false);
      }
    },
    [setSearchParams],
  );

  React.useEffect(() => {
    let mounted = true;
    if (!selectedDatasetId || pairsForDataset.length === 0) {
      setTrendDashboard(null);
      setRunDashboard(null);
      setSelectedPairId("");
      setSelectedAgentId(null);
      setSelectedRunId(null);
      setDashboardLoading(false);
      return;
    }

    const shellPair =
      findPairForAgent(pairsForDataset, DEMO_DEFAULT_AGENT_ID) ?? pairsForDataset[0]!;
    setRunDashboard(null);
    setSelectedPairId("");
    setSelectedAgentId(null);
    setSelectedRunId(null);

    const loadInitialRun = (shell: PairDashboardPayload) => {
      const urlAgent = searchParams.get("agent")?.trim() ?? "";
      const urlRun = searchParams.get("run")?.trim() ?? "";
      const urlPair = urlAgent ? findPairForAgent(pairsForDataset, urlAgent) : null;
      const hasUrlSelection = Boolean(urlAgent && urlRun && urlPair);
      const agentId = hasUrlSelection ? urlAgent : DEMO_DEFAULT_AGENT_ID;
      const runId = hasUrlSelection
        ? urlRun
        : (resolveDefaultAgentRunId(shell, DEMO_DEFAULT_AGENT_ID) ?? "");
      const pair = findPairForAgent(pairsForDataset, agentId) ?? shellPair;
      if (runId && pair) {
        void loadRunPanels(
          pair.id,
          runId,
          parsePairId(pair.id).agentId,
          !hasUrlSelection,
          shell,
        );
      }
    };

    const staleShell = getCachedTrendDashboard(selectedDatasetId);
    if (staleShell) {
      setTrendDashboard(staleShell);
      trendDashboardRef.current = staleShell;
      loadInitialRun(staleShell);
    }

    const loadDashboard = async () => {
      setDashboardLoading(!staleShell);
      setErrors([]);
      try {
        if (!staleShell) {
          const fastShell = await metricsApi.getPairDashboard(shellPair.id, null, false, true);
          if (!mounted || !fastShell) return;
          // Seed run panels immediately, but wait for scored trends so the
          // chart does not paint a partial series and then morph.
          trendDashboardRef.current = fastShell;
          loadInitialRun(fastShell);
        }

        const shell = await metricsApi.getPairDashboard(shellPair.id, null);
        if (!mounted || !shell) return;
        setCachedTrendDashboard(selectedDatasetId, shell);
        setTrendDashboard(shell);
        trendDashboardRef.current = shell;

        const latestRunId = resolveDefaultAgentRunId(shell, DEMO_DEFAULT_AGENT_ID);
        if (latestRunId || (searchParams.get("agent") && searchParams.get("run"))) {
          loadInitialRun(shell);
        } else if (mounted) {
          setRunDashboard(null);
          setSelectedPairId("");
          setSelectedAgentId(null);
          setSelectedRunId(null);
        }
      } catch (err) {
        if (!mounted) return;
        if (!staleShell) {
          const fallback = trendDashboardRef.current;
          if (fallback) {
            setTrendDashboard(fallback);
          } else {
            setTrendDashboard(null);
            setRunDashboard(null);
          }
        }
        const message = err instanceof Error ? err.message : "Failed to load dashboard.";
        setErrors([staleShell ? `Showing cached dashboard. ${message}` : message]);
      } finally {
        if (mounted) setDashboardLoading(false);
      }
    };
    void loadDashboard();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatasetId, pairsForDataset]);

  React.useEffect(() => {
    let mounted = true;
    if (!selectedDatasetId) {
      setComparisons([]);
      return;
    }
    void metricsApi
      .getDatasetComparisons(selectedDatasetId)
      .then((rows) => {
        if (mounted) setComparisons(rows);
      })
      .catch(() => {
        if (mounted) setComparisons([]);
      });
    return () => {
      mounted = false;
    };
  }, [selectedDatasetId]);

  const handleSelectDataset = (datasetId: string) => {
    loadSeqRef.current += 1;
    setSelectedDatasetId(datasetId);
    setErrors([]);
    const next = new URLSearchParams(searchParams);
    next.set("dataset", datasetId);
    next.delete("agent");
    next.delete("run");
    next.delete("pair");
    setSearchParams(next, { replace: true });
  };

  const handleSelectRun = React.useCallback(
    (agentId: string, runId: string) => {
      const pair = findPairForAgent(pairsForDataset, agentId);
      if (!pair || !runId) return;
      void loadRunPanels(pair.id, runId, agentId);
    },
    [pairsForDataset, loadRunPanels],
  );

  if (pairsLoading) {
    return (
      <div className={styles.dashboard}>
        <section className={styles.dashboardLoading} aria-live="polite" aria-busy="true">
          <div className={styles.dashboardLoadingSpinner} />
          <p className={styles.dashboardLoadingText}>Loading datasets...</p>
        </section>
      </div>
    );
  }

  if (allPairs.length === 0 || datasets.length === 0) {
    return (
      <div className={styles.dashboard}>
        <section className={styles.dashboardEmpty}>
          <p className={styles.dashboardEmptyText}>
            No datasets with Langfuse experiment scores.
            {langfuseHost
              ? ` API Langfuse host: ${langfuseHost}.`
              : " LANGFUSE_HOST is not set on the API."}{" "}
            It must be https://langfuse.trust-forge.ai (same project as local), not the in-compose
            langfuse-web.
          </p>
        </section>
        {errors.length > 0 ? (
          <section className={styles.metricsErrorBox}>
            {errors.map((err) => (
              <p key={err}>{err}</p>
            ))}
          </section>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <ExperimentDropdown
        title="Dashboard"
        selectLabel="Select dataset"
        options={datasets.map((dataset) => ({ id: dataset.id, label: dataset.label }))}
        selectedId={selectedDatasetId}
        onSelect={handleSelectDataset}
      />

      <LangfuseMetricsCharts
        trendDashboard={trendDashboard}
        runDashboard={runDashboard}
        comparisons={comparisons}
        loading={dashboardLoading && !trendDashboard}
        runLoading={runLoading}
        pairId={selectedPairId}
        selectedAgentId={selectedAgentId}
        selectedRunId={selectedRunId}
        onSelectRun={handleSelectRun}
      />

      {errors.length > 0 ? (
        <section className={styles.metricsErrorBox}>
          {errors.map((err) => (
            <p key={err}>{err}</p>
          ))}
        </section>
      ) : null}
    </div>
  );
};

export default LangfuseDashboardPage;
